import {createAction, props} from '@ngrx/store';
import {UUID} from '../../shared/types/uuid.type';
import {Cluster, CreateClusterPayload, DeleteClusterPayload} from '../../services/api/clusters/cluster.model';
import {ClusterRegion} from '../../services/api/vocabulary/cluster/regions/cluster.region.vocabulary.model';

/**
 * Action prefix for clusters
 */
const ACTION_PREFIX = '[Clusters]';

// ==================== Load All Clusters ====================

/**
 * Initiates loading of all clusters
 */
export const loadClusters = createAction(
  `${ACTION_PREFIX} Load All`
);

/**
 * Successfully loaded all clusters
 */
export const loadClustersSuccess = createAction(
  `${ACTION_PREFIX} Load All Success`,
  props<{ clusters: Cluster[] }>()
);

/**
 * Failed to load clusters
 */
export const loadClustersFailure = createAction(
  `${ACTION_PREFIX} Load All Failure`,
  props<{ error: any }>()
);

// ==================== Load Single Cluster by ID ====================

/**
 * Initiates loading of a single cluster by ID
 */
export const loadClusterById = createAction(
  `${ACTION_PREFIX} Load By ID`,
  props<{ id: string }>()
);

/**
 * Successfully loaded a single cluster
 */
export const loadClusterByIdSuccess = createAction(
  `${ACTION_PREFIX} Load By ID Success`,
  props<{ cluster: Cluster }>()
);

/**
 * Failed to load a cluster by ID
 */
export const loadClusterByIdFailure = createAction(
  `${ACTION_PREFIX} Load By ID Failure`,
  props<{ id: string; error: any }>()
);

// ==================== Create Cluster ====================

/**
 * Initiates creation of a new cluster
 */
export const createCluster = createAction(
  `${ACTION_PREFIX} Create`,
  props<{ payload: CreateClusterPayload }>()
);

/**
 * Successfully created a cluster
 */
export const createClusterSuccess = createAction(
  `${ACTION_PREFIX} Create Success`,
  props<{ cluster: Cluster }>()
);

/**
 * Failed to create cluster
 */
export const createClusterFailure = createAction(
  `${ACTION_PREFIX} Create Failure`,
  props<{ error: any }>()
);

// ==================== Delete Cluster ====================

/**
 * Initiates deletion of a cluster
 */
export const deleteCluster = createAction(
  `${ACTION_PREFIX} Delete`,
  props<{ payload: DeleteClusterPayload }>()
);

/**
 * Successfully deleted a cluster
 */
export const deleteClusterSuccess = createAction(
  `${ACTION_PREFIX} Delete Success`,
  props<{ id: string; cluster: Cluster }>()
);

/**
 * Failed to delete cluster
 */
export const deleteClusterFailure = createAction(
  `${ACTION_PREFIX} Delete Failure`,
  props<{ id: string; error: any }>()
);

// ==================== Start/Stop Cluster ====================

/**
 * Initiates starting a cluster
 */
export const startCluster = createAction(
  `${ACTION_PREFIX} Start`,
  props<{ id: UUID }>()
);

/**
 * Successfully started a cluster
 */
export const startClusterSuccess = createAction(
  `${ACTION_PREFIX} Start Success`,
  props<{ cluster: Cluster }>()
);

/**
 * Failed to start cluster
 */
export const startClusterFailure = createAction(
  `${ACTION_PREFIX} Start Failure`,
  props<{ id: UUID; error: any }>()
);

/**
 * Initiates stopping a cluster
 */
export const stopCluster = createAction(
  `${ACTION_PREFIX} Stop`,
  props<{ id: UUID }>()
);

/**
 * Successfully stopped a cluster
 */
export const stopClusterSuccess = createAction(
  `${ACTION_PREFIX} Stop Success`,
  props<{ cluster: Cluster }>()
);

/**
 * Failed to stop cluster
 */
export const stopClusterFailure = createAction(
  `${ACTION_PREFIX} Stop Failure`,
  props<{ id: UUID; error: any }>()
);

// ==================== Cluster Regions ====================

/**
 * Initiates loading of all cluster regions
 */
export const loadClusterRegions = createAction(
  `${ACTION_PREFIX} Load Regions`
);

/**
 * Successfully loaded all cluster regions
 */
export const loadClusterRegionsSuccess = createAction(
  `${ACTION_PREFIX} Load Regions Success`,
  props<{ regions: ClusterRegion[] }>()
);

/**
 * Failed to load cluster regions
 */
export const loadClusterRegionsFailure = createAction(
  `${ACTION_PREFIX} Load Regions Failure`,
  props<{ error: any }>()
);

// ==================== UI State Management ====================

/**
 * Selects a cluster
 */
export const selectCluster = createAction(
  `${ACTION_PREFIX} Select`,
  props<{ id: string | null }>()
);

/**
 * Clears all errors from the state
 */
export const clearClusterErrors = createAction(
  `${ACTION_PREFIX} Clear Errors`
);

/**
 * Resets the entire clusters state to initial values
 */
export const resetClustersState = createAction(
  `${ACTION_PREFIX} Reset State`
);

/**
 * Adds or updates a cluster in the state (e.g. from websocket)
 */
export const upsertCluster = createAction(
  `${ACTION_PREFIX} Upsert`,
  props<{ cluster: Cluster }>()
);

/**
 * Removes a cluster from the state
 */
export const removeCluster = createAction(
  `${ACTION_PREFIX} Remove`,
  props<{ id: string }>()
);
